import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { CustomerShell } from "@/components/CustomerShell";
import { rupee } from "@/lib/constants";
import { Wallet, ChevronRight, Loader2, ReceiptIndianRupee } from "lucide-react";

const PAID_STATES = ["PAID", "COMPLETED", "CLOSED", "INVOICE GENERATED"];

function payInfo(b) {
    const amt = b.total_amount ?? b.estimate?.total ?? b.estimated_total ?? b.amount ?? null;
    const ps = (b.payment_status || "").toUpperCase();
    let state = "Pending";
    let cls = "bg-warning/10 text-[#9a6b12]";
    if (ps === "PAID" || PAID_STATES.includes((b.status || "").toUpperCase())) { state = "Paid"; cls = "bg-success/10 text-success"; }
    else if (ps === "REFUNDED") { state = "Refunded"; cls = "bg-navy/10 text-navy"; }
    else if (!amt) { state = "No dues"; cls = "bg-navy/5 text-navy/50"; }
    return { amt, state, cls };
}

export default function Payments() {
    const [rows, setRows] = useState(null);
    const { t } = useLang();
    const nav = useNavigate();

    useEffect(() => { api.get("/bookings").then((r) => setRows(r.data || [])).catch(() => setRows([])); }, []);

    const list = (rows || []).filter((b) => payInfo(b).amt != null);

    return (
        <CustomerShell>
            <div className="rounded-b-3xl bg-navy px-5 pb-6 pt-6 text-white">
                <div className="flex items-center gap-2">
                    <Wallet size={20} className="text-aqua" />
                    <h1 className="font-heading text-2xl font-extrabold">{t("Payments")}</h1>
                </div>
                <p className="mt-1 text-sm text-white/60">{t("Your payments & invoices")}</p>
            </div>

            <div className="space-y-3 px-5 py-5">
                {rows === null && <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-teal" /></div>}
                {rows && list.length === 0 && (
                    <div className="flex flex-col items-center py-16 text-center" data-testid="payments-empty">
                        <div className="rounded-2xl bg-aqua-light p-5"><ReceiptIndianRupee size={30} className="text-teal" /></div>
                        <h3 className="mt-4 font-heading text-lg font-bold text-navy">{t("No payments yet")}</h3>
                        <p className="mt-1 text-sm text-navy/50">{t("Your payment history will appear here.")}</p>
                    </div>
                )}
                {list.map((b) => {
                    const { amt, state, cls } = payInfo(b);
                    return (
                        <button
                            key={b.id}
                            data-testid={`payment-row-${b.booking_id}`}
                            onClick={() => nav(`/bookings/${b.id}`)}
                            className="flex w-full items-center gap-3 rounded-2xl border border-border bg-white p-4 text-left shadow-[0_4px_16px_rgba(0,156,149,0.05)] transition-transform active:scale-[0.98]"
                        >
                            <div className="flex-1">
                                <span className="font-heading text-sm font-extrabold text-navy">{b.booking_id}</span>
                                <p className="mt-0.5 text-sm font-medium text-navy/80">{b.service_name}</p>
                                <p className="text-xs text-navy/45">{b.preferred_date} · {b.preferred_window}</p>
                            </div>
                            <div className="text-right">
                                <p className="font-heading text-base font-extrabold text-navy">{rupee(amt)}</p>
                                <span className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold ${cls}`}>{state}</span>
                            </div>
                            <ChevronRight size={18} className="text-navy/25" />
                        </button>
                    );
                })}
            </div>
        </CustomerShell>
    );
}
